// src/services/categoryService.js
import axios from "axios";
import { API_URL } from "../constant";

const API_BASE_URL = API_URL || "https://staging.api.topcoinuk.com/api";

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("auth_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

export const categoryService = {
  // ============ CATEGORY METHODS ============

  getAllCategories: async (params = {}) => {
    const response = await api.get("/superadmin/categories", { params });
    return response.data;
  },

  getCategory: async (categoryId) => {
    const response = await api.get(`/superadmin/categories/${categoryId}`);
    return response.data;
  },

  createCategory: async (categoryData) => {
    const formData = new FormData();

    // Append basic fields
    Object.keys(categoryData).forEach((key) => {
      if (key === "image" && categoryData[key] instanceof File) {
        formData.append("image", categoryData[key]);
      } else if (key === "meta_keywords" && Array.isArray(categoryData[key])) {
        formData.append("meta_keywords", JSON.stringify(categoryData[key]));
      } else if (
        categoryData[key] !== null &&
        categoryData[key] !== undefined
      ) {
        formData.append(key, categoryData[key]);
      }
    });

    const response = await api.post("/superadmin/categories", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  updateCategory: async (categoryId, categoryData) => {
  const formData = new FormData();

  // Check if categoryData is already FormData
  if (categoryData instanceof FormData) {
    // Add _method=PUT for Laravel to treat POST as PUT
    categoryData.append("_method", "POST");
    
    const response = await api.post(
      `/superadmin/categories/${categoryId}`,
      categoryData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  }

  // If categoryData is an object, convert to FormData
  Object.keys(categoryData).forEach((key) => {
    if (key === "image" && categoryData[key] instanceof File) {
      formData.append("image", categoryData[key]);
    } else if (key === "meta_keywords" && Array.isArray(categoryData[key])) {
      formData.append("meta_keywords", JSON.stringify(categoryData[key]));
    } else if (categoryData[key] !== null && categoryData[key] !== undefined) {
      formData.append(key, categoryData[key]);
    }
  });

  // Add _method=PUT for Laravel
  formData.append("_method", "POST");

  const response = await api.post(
    `/superadmin/categories/${categoryId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
},

  deleteCategory: async (categoryId) => {
    const response = await api.delete(`/superadmin/categories/${categoryId}`);
    return response.data;
  },

  forceDeleteCategory: async (categoryId) => {
    const response = await api.delete(
      `/superadmin/categories/force/${categoryId}`,
    );
    return response.data;
  },

  restoreCategory: async (categoryId) => {
    const response = await api.post(
      `/superadmin/categories/restore/${categoryId}`,
    );
    return response.data;
  },

  bulkDeleteCategories: async (categoryIds, force = false) => {
    const response = await api.post("/superadmin/categories/bulk-delete", {
      category_ids: categoryIds,
      force,
    });
    return response.data;
  },

  updateCategoryStatus: async (
    categoryId,
    status,
    updateSubcategories = false,
  ) => {
    const response = await api.post(
      `/superadmin/categories/${categoryId}/status`,
      {
        status,
        update_subcategories: updateSubcategories,
      },
    );
    return response.data;
  },

  bulkUpdateCategoryStatus: async (
    categoryIds,
    status,
    updateSubcategories = false,
  ) => {
    const response = await api.post("/superadmin/categories/bulk-status", {
      category_ids: categoryIds,
      status,
      update_subcategories: updateSubcategories,
    });
    return response.data;
  },

  getCategoryStatistics: async () => {
    const response = await api.get("/superadmin/categories/statistics/summary");
    return response.data;
  },

  searchCategories: async (params = {}) => {
    const response = await api.get("/superadmin/categories/search/quick", {
      params,
    });
    return response.data;
  },

  exportCategories: async (params = {}) => {
    const response = await api.get("/superadmin/categories/export/data", {
      params,
      responseType: params.format === "csv" ? "blob" : "json",
    });
    return response.data;
  },

  // ============ SUBCATEGORY METHODS ============

  getAllSubcategories: async (params = {}) => {
    const response = await api.get("/superadmin/subcategories", { params });
    return response.data;
  },

  getSubcategory: async (subcategoryId) => {
    const response = await api.get(
      `/superadmin/subcategories/${subcategoryId}`,
    );
    return response.data;
  },

  createSubcategory: async (data, imageFile = null) => {
    const formData = new FormData();

    // Handle if data is already FormData
    if (data instanceof FormData) {
      return api
        .post("/superadmin/subcategories", data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => response.data);
    }

    // Handle regular object with optional separate image file
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined) {
        if (key === "meta_keywords" && Array.isArray(data[key])) {
          formData.append(key, JSON.stringify(data[key]));
        } else if (key === "image" && data[key] instanceof File) {
          formData.append("image", data[key]);
        } else {
          formData.append(key, data[key]);
        }
      }
    });

    // Append image file if provided separately
    if (imageFile && imageFile instanceof File) {
      formData.append("image", imageFile);
    }

    const response = await api.post("/superadmin/subcategories", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  updateSubcategory: async (subcategoryId, subcategoryData) => {
    // If it's already FormData, use it directly
    if (subcategoryData instanceof FormData) {
        const response = await api.post(`/superadmin/subcategories/${subcategoryId}`, subcategoryData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    }
    
    // Otherwise, convert object to FormData
    const formData = new FormData();
    
    Object.keys(subcategoryData).forEach(key => {
        if (key === 'image' && subcategoryData[key] instanceof File) {
            formData.append('image', subcategoryData[key]);
        } else if (key === 'meta_keywords' && Array.isArray(subcategoryData[key])) {
            formData.append('meta_keywords', JSON.stringify(subcategoryData[key]));
        } else if (subcategoryData[key] !== null && subcategoryData[key] !== undefined) {
            formData.append(key, subcategoryData[key]);
        }
    });


    formData.append('_method', 'POST');

    const response = await api.post(`/superadmin/subcategories/${subcategoryId}`, formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    return response.data;
},

  deleteSubcategory: async (subcategoryId) => {
    const response = await api.delete(
      `/superadmin/subcategories/${subcategoryId}`,
    );
    return response.data;
  },

  forceDeleteSubcategory: async (subcategoryId) => {
    const response = await api.delete(
      `/superadmin/subcategories/force/${subcategoryId}`,
    );
    return response.data;
  },

  restoreSubcategory: async (subcategoryId) => {
    const response = await api.post(
      `/superadmin/subcategories/restore/${subcategoryId}`,
    );
    return response.data;
  },

  bulkDeleteSubcategories: async (subcategoryIds, force = false) => {
    const response = await api.post("/superadmin/subcategories/bulk-delete", {
      subcategory_ids: subcategoryIds,
      force,
    });
    return response.data;
  },

  updateSubcategoryStatus: async (subcategoryId, status) => {
    const response = await api.post(
      `/superadmin/subcategories/${subcategoryId}/status`,
      {
        status,
      },
    );
    return response.data;
  },

  bulkUpdateSubcategoryStatus: async (subcategoryIds, status) => {
    const response = await api.post("/superadmin/subcategories/bulk-status", {
      subcategory_ids: subcategoryIds,
      status,
    });
    return response.data;
  },

  getSubcategoriesByCategory: async (categoryId, params = {}) => {
    const response = await api.get(
      `/superadmin/subcategories/category/${categoryId}`,
      { params },
    );
    return response.data;
  },

  getSubcategoryStatistics: async () => {
    const response = await api.get(
      "/superadmin/subcategories/statistics/summary",
    );
    return response.data;
  },

  searchSubcategories: async (params = {}) => {
    const response = await api.get("/superadmin/subcategories/search/quick", {
      params,
    });
    return response.data;
  },

  exportSubcategories: async (params = {}) => {
    const response = await api.get("/superadmin/subcategories/export/data", {
      params,
      responseType: params.format === "csv" ? "blob" : "json",
    });
    return response.data;
  },
};
